/* ============================================================================
   Jelkin bag ladder — drawer quantity stepper
   ============================================================================
   Why this file exists
   --------------------
   The drawer's +/- used to be plain Dawn `cart/change.js` quantity edits. So
   "Just him" x2 charged 2 x $89 = $178, while the advertised 2-bag set price
   ("Him + a sibling") is $169 — two paths to the same bag count, two prices.

   The reference store solves this with a cart discount. This store deliberately
   has no automatic discounts (the prices are real product prices), so instead we
   re-compose the cart onto the SAME real bundle products the PDP's "Choose your
   set" already adds. Every path then lands on the advertised ladder:

       1 bag  ->  single colour product              $89
       2 bags ->  The Duo                            $169
       3 bags ->  The Trio                           $249
       n bags ->  The Trio + (n-3) x Extra bag       $249 + (n-3) x $83

   Colours are preserved. A bag added with "+" repeats the colour of the line you
   clicked — which is exactly what the reference store does (its "+" keeps the
   same product and colour and only the quantity and the set price change).

   Safety
   ------
   Nothing here is allowed to leave the cart half-rewritten. If the variant map
   is missing, a colour is unknown, we cannot tell which line was clicked, or any
   request fails, we fall back to a plain `cart/change.js` quantity edit — i.e.
   the original Dawn behaviour — and re-render the drawer.

   Cart mutations are additive-first: the target lines are added, then the stale
   lines are removed. A failure therefore leaves the cart priced HIGH, never low.
   ========================================================================== */

(function () {
  'use strict';

  var MAP_SEL = 'script[data-jc-bags-map]';

  /* ---------------------------------------------------------------- helpers */

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.text().then(function (txt) {
        var json;
        try { json = JSON.parse(txt); } catch (e) { json = null; }
        if (!res.ok || (json && json.status)) {
          throw new Error((json && (json.description || json.message)) || 'cart error ' + res.status);
        }
        return json;
      });
    });
  }

  var _map = null;
  var _mapEl = null;

  function bagMap() {
    var el = document.querySelector(MAP_SEL);
    if (!el) return null;
    if (_map && _mapEl === el) return _map;
    var d;
    try { d = JSON.parse(el.textContent); } catch (e) { return null; }
    if (!d || !d.colours || !d.colours.length || !d.duo || !d.trio || !d.extra) return null;

    var byVariant = {}, byName = {}, bySlug = {};
    d.colours.forEach(function (c) {
      if (!c || !c.variant || !c.name) return;
      c.variant = String(c.variant);
      byVariant[c.variant] = c;
      byName[c.name] = c;
      if (c.slug) bySlug[c.slug] = c;
    });
    d.byVariant = byVariant;
    d.byName = byName;
    d.bySlug = bySlug;
    d.duo = String(d.duo);
    d.trio = String(d.trio);
    d.extra = String(d.extra);
    _map = d;
    _mapEl = el;
    return d;
  }

  /* colours carried by one cart line, in bag order ([] = not a bag line) */
  function lineColours(item, M) {
    var vid = String(item.variant_id);

    if (M.byVariant[vid]) {
      var out = [];
      for (var i = 0; i < item.quantity; i++) out.push(M.byVariant[vid].name);
      return out;
    }

    var per = vid === M.duo ? 2 : vid === M.trio ? 3 : vid === M.extra ? 1 : 0;
    if (!per) return null; /* some other product — leave it alone */

    var pairs = [];
    var props = item.properties || {};
    Object.keys(props).forEach(function (k) {
      var m = /^Bag\s+(\d+)$/.exec(k);
      if (m && props[k]) pairs.push([parseInt(m[1], 10), props[k]]);
    });
    pairs.sort(function (a, b) { return a[0] - b[0]; });
    var colours = pairs.map(function (p) { return p[1]; });

    /* The "Siblings?" tile writes only the bag it adds (e.g. a Duo carrying just
       `Bag 2`). The bundle size is what defines the bag count, so pad the list up
       to per x quantity — repeating the colour we do know. Nothing to go on at
       all -> treat the line as unknown and let the plain fallback handle it. */
    var need = per * (item.quantity || 1);
    if (!colours.length) return null;
    while (colours.length < need) colours.push(colours[colours.length - 1]);
    return colours.slice(0, need);
  }

  /* ordered bag segments of the cart + the flat colour list */
  function segments(cart, M) {
    var segs = [], all = [];
    (cart.items || []).forEach(function (it, i) {
      var c = lineColours(it, M);
      if (!c) return;
      segs.push({
        index: i + 1,                 /* Dawn's 1-based `line` / data-index */
        key: it.key,
        variant: String(it.variant_id),
        start: all.length,
        colours: c,
      });
      all = all.concat(c);
    });
    return { segs: segs, all: all };
  }

  function bagProps(list) {
    var p = {};
    list.forEach(function (name, i) { p['Bag ' + (i + 1)] = name; });
    return p;
  }

  /* the canonical set composition for a list of bag colours */
  function compose(colours, M) {
    /* 每个颜色一行（原站方式）：价格由后台自动折扣落档 */
    var q = {}, order = [], i;
    for (i = 0; i < colours.length; i++) {
      if (q[colours[i]] === undefined) { q[colours[i]] = 0; order.push(colours[i]); }
      q[colours[i]] += 1;
    }
    var out = [];
    for (i = 0; i < order.length; i++) {
      var c = M.byName[order[i]];
      if (!c) return null;              /* unknown colour — let fallback handle it */
      out.push({ id: c.variant, quantity: q[order[i]], properties: {} });
    }
    return out;
  }

  function bagOnly(props) {
    var out = {};
    Object.keys(props || {}).forEach(function (k) {
      if (/^Bag\s+\d+$/.test(k) && props[k]) out[k] = props[k];
    });
    return out;
  }

  function sig(id, props) {
    var p = bagOnly(props), keys = Object.keys(p).sort();
    return String(id) + '|' + keys.map(function (k) { return k + '=' + p[k]; }).join('&');
  }

  /* ------------------------------------------------------------- mutations */

  /* one pass of the diff between the cart as it is and the target composition */
  function runPlan(cart, target) {
    var want = {}, have = {};
    target.forEach(function (t) { want[sig(t.id, t.properties)] = t; });

    var removals = [], adjusts = [];
    (cart.items || []).forEach(function (it) {
      var s = sig(it.variant_id, it.properties);
      have[s] = (have[s] || 0) + it.quantity;
      if (want[s]) {
        if (it.quantity !== want[s].quantity) {
          adjusts.push({ key: it.key, quantity: want[s].quantity });
        }
      } else {
        removals.push(it.key);
      }
    });

    var additions = [];
    target.forEach(function (t) {
      var s = sig(t.id, t.properties);
      var need = t.quantity - (have[s] || 0);
      if (need > 0) additions.push({ id: t.id, quantity: need, properties: t.properties });
    });

    /* additive first, destructive second: if a removal fails the cart is left
       priced HIGH (a customer tells us) rather than silently low. */
    var chain = Promise.resolve();
    if (additions.length) {
      chain = chain.then(function () { return post('/cart/add.js', { items: additions }); });
    }
    removals.forEach(function (key) {
      chain = chain.then(function () { return post('/cart/change.js', { id: key, quantity: 0 }); });
    });
    adjusts.forEach(function (a) {
      chain = chain.then(function () { return post('/cart/change.js', { id: a.key, quantity: a.quantity }); });
    });
    return chain;
  }

  function matches(cart, target) {
    var want = {};
    target.forEach(function (t) { want[sig(t.id, t.properties)] = t.quantity; });
    var seen = {}, ok = true;
    (cart.items || []).forEach(function (it) {
      var s = sig(it.variant_id, it.properties);
      if (want[s] === undefined || it.quantity !== want[s]) ok = false;
      seen[s] = (seen[s] || 0) + it.quantity;
    });
    target.forEach(function (t) {
      var s = sig(t.id, t.properties);
      if (seen[s] !== t.quantity) ok = false;
    });
    return ok;
  }

  function notice(msg) {
    var box = document.getElementById('CartDrawer-CartErrors');
    if (box) box.textContent = msg;
  }

  /* Run the plan, then re-read the cart and confirm we actually landed on the
     target. A single dropped request used to leave the bag priced high with no
     trace; now we retry once and say so if it still does not add up. */
  function converge(cart, target, M, tries) {
    return runPlan(cart, target)
      .catch(function () { /* a dropped request is handled by the check below */ })
      .then(function () {
        return fetch('/cart.js', { cache: 'no-store' }).then(function (r) { return r.json(); });
      }).then(function (now) {
      if (matches(now, target)) return true;
      if (tries > 0) return converge(now, target, M, tries - 1);
      notice(__t('We could not line the bag up exactly — please open the bag and remove the extra item.'));
      return false;
    });
  }

  function pull(url, sel) {
    return fetch(url, { headers: { Accept: 'text/html' }, cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html').querySelector(sel); });
  }

  /* Both sections are independent — fetch them together. Sequential fetches
     used to add ~350ms of dead time to every step on a real network. */
  function refresh() {
    return Promise.all([
      pull((window.jcRoot||'/')+'cart?section_id=cart-drawer', '.drawer__inner'),
      pull((window.jcRoot||'/')+'cart?section_id=cart-icon-bubble', '#cart-icon-bubble'),
    ]).then(function (parts) {
      var inner = parts[0], live = document.querySelector('cart-drawer .drawer__inner');
      if (inner && live) {
        live.className = inner.className;
        live.innerHTML = inner.innerHTML;
      }
      var bub = parts[1], liveBub = document.querySelector('#cart-icon-bubble');
      if (bub && liveBub) liveBub.outerHTML = bub.outerHTML;
    }).catch(function () { /* the drawer re-renders on the next event anyway */ });
  }

  /* plain Dawn behaviour — used whenever we cannot safely re-compose */
  function plain(field, nextQuantity) {
    var line = parseInt(field.getAttribute('data-index') || '0', 10);
    if (!line) return Promise.resolve();
    return post('/cart/change.js', { line: line, quantity: nextQuantity })
      .then(refresh)
      .catch(refresh);
  }

  function plainFromButton(btn, field) {
    var q = parseInt(field.value, 10);
    if (isNaN(q) || q < 0) q = 0;
    q = btn && btn.name === 'plus' ? q + 1 : Math.max(0, q - 1);
    return plain(field, q);
  }

  /* --------------------------------------------------------------- stepping */

  function recompose(cart, M, edit) {
    var S = segments(cart, M);
    if (!S.segs.length) return null;

    var seg = null, i;
    for (i = 0; i < S.segs.length; i++) if (S.segs[i].index === edit.index) seg = S.segs[i];
    if (!seg) return null;

    var all = S.all.slice();
    var start = seg.start;
    var len = seg.colours.length;
    var seed = seg.colours[len - 1];

    if (edit.delta === 1) {
      all.splice(start + len, 0, seed);
    } else if (edit.delta === -1) {
      if (!len) return null;
      all.splice(start + len - 1, 1);
    } else {
      var q = edit.quantity;
      if (!(q >= 0)) return null;
      var repl = [];
      for (i = 0; i < q; i++) repl.push(seed);
      all = all.slice(0, start).concat(repl, all.slice(start + len));
    }
    if (!all.length && S.all.length === 0) return null;
    return compose(all, M);
  }

  function step(btn, field) {
    var M = bagMap();
    if (!M) return plainFromButton(btn, field);

    var index = parseInt(field.getAttribute('data-index') || '0', 10);
    if (!index) return plainFromButton(btn, field);

    return fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var target = recompose(cart, M, { index: index, delta: btn.name === 'plus' ? 1 : -1 });
        notice('');
        if (!target) return plainFromButton(btn, field);
        if (!target.length) return plain(field, 0);
        return converge(cart, target, M, 1).then(refresh);
      })
      .catch(function () { return refresh(); });
  }

  function typed(field, forced) {
    var M = bagMap();
    var q = forced === undefined || forced === null ? parseInt(field.value, 10) : forced;
    if (isNaN(q) || q < 0) q = 0;
    if (!M) return plain(field, q);

    var index = parseInt(field.getAttribute('data-index') || '0', 10);
    if (!index) return plain(field, q);

    return fetch('/cart.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var target = recompose(cart, M, { index: index, quantity: q });
        notice('');
        if (!target) return plain(field, q);
        if (!target.length) return plain(field, 0);
        return converge(cart, target, M, 1).then(refresh);
      })
      .catch(function () { return refresh(); });
  }

  /* ----------------------------------------------------------------- wiring */

  /* ----------------------------------------------------------------- queue */

  /* A step costs ~3s on a live network (read → add → remove → verify → render).
     Dropping clicks that arrive during that window meant a customer tapping
     "+" twice only ever got one bag. Intents are queued instead: each one
     re-reads the cart when its turn comes, so a burst of taps lands on the
     right total however long the network takes. */
  var busy = false, queue = [], MAX_PENDING = 2;

  var CSS_ID = 'jc-bagfit-busy';
  function markBusy(on) {
    if (on && !document.getElementById(CSS_ID)) {
      var s = document.createElement('style');
      s.id = CSS_ID;
      s.textContent = 'cart-drawer.jc-stepping .quantity__input{opacity:.5}' +
        'cart-drawer.jc-stepping .quantity__button{cursor:progress}';
      document.head.appendChild(s);
    }
    var d = document.querySelector('cart-drawer');
    if (d) d.classList.toggle('jc-stepping', !!on);
  }

  function drain() {
    if (busy) return;
    var job = queue.shift();
    if (!job) { markBusy(false); return; }
    busy = true;
    markBusy(true);
    var done = function () { busy = false; drain(); };
    Promise.resolve().then(job).then(done, done);
  }

  function enqueue(job) {
    if (queue.length >= MAX_PENDING) return; /* runaway tapping guard */
    queue.push(job);
    drain();
  }

  /* The drawer body is replaced on every render, so never hold on to the old
     node: look the line up again by index when the job actually runs. */
  function fieldFor(index) {
    var list = document.querySelectorAll('cart-drawer .quantity__input'), i;
    for (i = 0; i < list.length; i++) {
      if (parseInt(list[i].getAttribute('data-index') || '0', 10) === index) return list[i];
    }
    return list.length ? list[0] : null;
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var btn = t.closest('cart-drawer .quantity__button');
    if (!btn) return;
    var input = btn.closest('quantity-input');
    var field = input && input.querySelector('.quantity__input');
    if (!field) return;

    /* take the click away from Dawn's QuantityInput before it can fire */
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    var kind = btn.name;
    var index = parseInt(field.getAttribute('data-index') || '0', 10);
    enqueue(function () {
      var f = fieldFor(index);
      if (!f) return null;
      return (kind === 'plus' || kind === 'minus') ? step(btn, f) : plainFromButton(btn, f);
    });
  }, true);

  document.addEventListener('change', function (e) {
    var field = e.target;
    if (!field || !field.classList || !field.classList.contains('quantity__input')) return;
    if (typeof field.closest !== 'function' || !field.closest('cart-drawer')) return;

    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    var index = parseInt(field.getAttribute('data-index') || '0', 10);
    var q = parseInt(field.value, 10); /* the node may be gone before the job runs */
    enqueue(function () {
      var f = fieldFor(index);
      if (!f) return null;
      return typed(f, q);
    });
  }, true);
})();
